import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@components/utils/classname';

import { Spinner } from './spinner';
import { getButtonClasses } from './styles/button-styles';

const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md whitespace-nowrap transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // XDS variants (based on Figma design system)
        // Using utility functions from button-styles.ts for maintainability
        primary: getButtonClasses('primary', 'solid'),
        secondary: getButtonClasses('secondary', 'solid'),
        tertiary: getButtonClasses('tertiary', 'solid'),
        success: getButtonClasses('success', 'solid'),
        warning: getButtonClasses('warning', 'solid'),
        destructive: getButtonClasses('destructive', 'solid'),
        // Legacy variants for backward compatibility
        default: 'bg-primary hover:bg-primary/90 text-primary-foreground',
        outline:
          'hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:hover:bg-input/50 border shadow-xs',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-brand-foreground underline-offset-4 hover:underline',
      },
      size: {
        // XDS Button sizes:
        // xxs: 24px height, Label 2 (12px)
        // xs: 28px height, Label 2 (12px)
        // sm: 32px height, Label 1 (14px)
        // md: 36px height, Label 1 (14px)
        // lg: 40px height, Label 1 (14px)
        xxs: 'text-label2 h-6 gap-1 rounded px-2 has-[>svg]:px-1.5',
        xs: 'text-label2 h-7 gap-1.5 rounded px-2.5 has-[>svg]:px-2',
        sm: 'text-label1 h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        md: 'text-label1 h-9 px-4 py-2 has-[>svg]:px-3',
        lg: 'text-label1 h-10 rounded-md px-6 has-[>svg]:px-4',
        'lg-mobile': 'text-label1 h-11 rounded-md px-6 has-[>svg]:px-4',
        // Legacy size for backward compatibility
        default: 'text-label1 h-9 px-4 py-2 has-[>svg]:px-3',
        icon: 'size-9',
      },
      ghost: {
        // Ghost modifier: removes background, adds hover background, uses base color for text
        // Can be combined with any variant (e.g., primary + ghost, warning + ghost)
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Ghost variants using utility functions from button-styles.ts
      {
        variant: 'primary',
        ghost: true,
        className: getButtonClasses('primary', 'ghost'),
      },
      {
        variant: 'secondary',
        ghost: true,
        className: getButtonClasses('secondary', 'ghost'),
      },
      {
        variant: 'tertiary',
        ghost: true,
        className: getButtonClasses('tertiary', 'ghost'),
      },
      {
        variant: 'success',
        ghost: true,
        className: getButtonClasses('success', 'ghost'),
      },
      {
        variant: 'warning',
        ghost: true,
        className: getButtonClasses('warning', 'ghost'),
      },
      {
        variant: 'destructive',
        ghost: true,
        className: getButtonClasses('destructive', 'ghost'),
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      ghost: false,
    },
  },
);

function Button({
  className,
  variant,
  size,
  ghost,
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Shows a loading spinner and disables the button */
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';
  const isDisabled = disabled || loading;

  // When asChild is true, we can't add wrapper elements - Radix Slot requires a single child
  // In this case, we render children directly (loading spinner replaces content)
  if (asChild) {
    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, ghost, className }))}
        disabled={isDisabled}
        aria-busy={loading}
        {...props}
      >
        {children}
      </Comp>
    );
  }

  // When loading, wrap children to prevent layout shift
  // When not loading, render children directly to preserve flexbox layout
  if (loading) {
    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, ghost, className }))}
        disabled={isDisabled}
        aria-busy={loading}
        {...props}
      >
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner className="size-5" />
        </span>
        <span className="invisible contents">{children}</span>
      </Comp>
    );
  }

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, ghost, className }))}
      disabled={isDisabled}
      {...props}
    >
      {children}
    </Comp>
  );
}

export { Button, buttonVariants };
